import fontFaces from './fontFaces.v1.json';
import fontList from './fonts.v1.json';
import { fontBase } from './assetBase';
import { SCHEMA_TOKENS, ShopTheme, TOKEN_NAMES } from './shopTheme';

// #189 (ra#499 S3): the layer is ONE <style data-shop-theme> plus an attribute
// on <html>. The selector outranks `.xc-portal` of portal.css and of the theme
// link in any order; declared on html too, the tokens reach AddressModal, whose
// portal root has no `.xc-portal`. No layer: no attribute, no style, nothing
// else: every rule of shopThemeLayer.css is gated on that attribute, so a shop
// without a theme stays pixel-identical.

const SCOPE = 'html[data-xc-shop-theme]';

interface FontFace {
  file: string;
  subset: string;
  weight: string;
}
const FAMILIES = fontFaces.families as Record<
  string,
  { slug: string; faces: FontFace[] }
>;
const UNICODE_RANGES = fontFaces.unicode_ranges as Record<string, string>;
const WEIGHTS = new Map(
  (fontList as { family: string; weights: number[] }[]).map(entry => [
    entry.family,
    entry.weights,
  ])
);

// the weights the portal self-hosts for a family; undefined = not offered here
export const fontWeightsOf = (family: string): number[] | undefined =>
  Object.prototype.hasOwnProperty.call(FAMILIES, family)
    ? WEIGHTS.get(family)
    : undefined;

const fontFaceRules = (families: string[]): string =>
  families
    .filter(family => fontWeightsOf(family) !== undefined)
    .flatMap(family => {
      const { slug, faces } = FAMILIES[family];
      return faces.map(
        face =>
          `@font-face{font-family:'${family}';font-style:normal;` +
          `font-weight:${face.weight};font-display:swap;` +
          `src:url(${fontBase()}/${slug}/${face.file}) format('woff2');` +
          `unicode-range:${UNICODE_RANGES[face.subset]}}`
      );
    })
    .join('\n');

// a data URI cannot take var(): the check glyph is built from the validated ink
const checkGlyph = (ink: string): string =>
  `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e` +
  `%3cpath fill='none' stroke='%23${ink.slice(1)}' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='m6 10 3 3 6-6'/%3e%3c/svg%3e")`;

const cssOf = ({ tokens, fonts }: ShopTheme): string => {
  const effective = TOKEN_NAMES.map(
    name => `${name}:${tokens[name] ?? SCHEMA_TOKENS[name].default}`
  ).join(';');
  const ink =
    tokens['--xc-accent-ink'] ?? SCHEMA_TOKENS['--xc-accent-ink'].default;
  const rules = [
    fontFaceRules(fonts),
    `${SCOPE},${SCOPE} .xc-portal{${effective}}`,
    `${SCOPE} .form-check-input:checked[type=checkbox]{--bs-form-check-bg-image:${checkGlyph(ink)}}`,
  ];
  // version2's .xc-card has no shadow: a themed shop that keeps the default
  // gets none; the card shadow is the merchant's explicit choice
  if (tokens['--xc-shadow'] !== undefined) {
    rules.push(`${SCOPE} .xc-portal .xc-card{box-shadow:var(--xc-shadow)}`);
  }
  return rules.filter(Boolean).join('\n');
};

export const applyShopTheme = (theme: ShopTheme | null): void => {
  const root = document.documentElement;
  const existing = document.head.querySelector<HTMLStyleElement>(
    'style[data-shop-theme]'
  );
  if (theme === null) {
    delete root.dataset.xcShopTheme;
    existing?.remove();
    return;
  }
  root.dataset.xcShopTheme = '1';
  const style = existing ?? document.createElement('style');
  style.setAttribute('data-shop-theme', '');
  style.textContent = cssOf(theme);
  if (existing === null) document.head.appendChild(style);
};
